/*
  Izračuni nad napredkom tečajnika.

  Vse funkcije so čiste: notri gredo podatki, ven pride rezultat. Nič ne
  kličejo in nič ne shranjujejo. Zato jih testi lahko preverijo brez baze
  in brez brskalnika — in zato se ob prenovi videza ne morejo tiho pokvariti.

  Oblika `prog`:
    { [exercise_id]: { subs: [{done, date, status, comment, ...}], comment,
                       instructor_comment } }
  `status` je null | 'pending' | 'confirmed' | 'rejected'.
*/

import { ZDRAVNISKI_VELJAVNOST_MESECEV } from '../config.js';
import { DEFAULT_PROGRAM, POGOJI_PRIVZETO } from './program.js';
import { addMonths, ageOf, daysUntil, fmtDate } from './format.js';
import { t } from '../i18n/index.js';

/*
  Program in pogoji so parametri: vsaka šola ima svoj program (program.js)
  in svoje pogoje za izpit. Brez njih veljata Perutov program in privzeti
  pogoji, zato stari klici še delujejo.
*/

/* ── Štetje pod-vaj ─────────────────────────────────────────
   „done“ šteje potrjene IN tiste, ki jih je tečajnik odkljukal.
   „confirmed“ šteje samo tisto, kar je inštruktor potrdil.       */

export const subDone = (ex, prog) => {
  const ep = prog?.[ex.id];
  if (!ep?.subs) return 0;
  return ex.subs.filter((_, i) => ep.subs[i]?.status === 'confirmed' || ep.subs[i]?.done).length;
};

export const subPending = (ex, prog) => {
  const ep = prog?.[ex.id];
  if (!ep?.subs) return 0;
  return ex.subs.filter((_, i) => ep.subs[i]?.status === 'pending' && ep.subs[i]?.done).length;
};

export const subConfirmed = (ex, prog) => {
  const ep = prog?.[ex.id];
  if (!ep?.subs) return 0;
  return ex.subs.filter((_, i) => ep.subs[i]?.status === 'confirmed').length;
};

export const subRejected = (ex, prog) => {
  const ep = prog?.[ex.id];
  if (!ep?.subs) return 0;
  return ex.subs.filter((_, i) => ep.subs[i]?.status === 'rejected').length;
};

export const totalSubs = (list) => list.reduce((a, ex) => a + ex.subs.length, 0);
export const totalSubDone = (list, prog) => list.reduce((a, ex) => a + subDone(ex, prog), 0);
export const totalSubPending = (list, prog) => list.reduce((a, ex) => a + subPending(ex, prog), 0);
export const totalSubConfirmed = (list, prog) =>
  list.reduce((a, ex) => a + subConfirmed(ex, prog), 0);
export const totalSubRejected = (list, prog) =>
  list.reduce((a, ex) => a + subRejected(ex, prog), 0);

/** Koliko vsega čaka inštruktorja pri enem tečajniku (vaje + leti). */
export const pendingCountFor = (prog, fl, program = DEFAULT_PROGRAM) =>
  totalSubPending(program.all, prog ?? {}) +
  (fl ?? []).filter((f) => f.status === 'pending').length;

/* ── Vrstni red v seznamu ───────────────────────────────────
   Najnovejša številka zgoraj, brez številke spodaj, testni na dnu. */

export const sortByStevilka = (a, b) => {
  if (!!a.testni !== !!b.testni) return a.testni ? 1 : -1;
  if (a.stevilka == null && b.stevilka == null) return 0;
  if (a.stevilka == null) return 1;
  if (b.stevilka == null) return -1;
  return b.stevilka - a.stevilka;
};

/* ── Zdravniško spričevalo ───────────────────────────────────
   Ne zadošča, da je dokument naložen — pregled ne sme biti starejši od
   24 mesecev. Datum pregleda vpiše inštruktor (profiles.zdravniski_datum);
   datum nalaganja fotografije ni isto kot datum pregleda.               */

export function medicalStatus(u, docMap, meseci = ZDRAVNISKI_VELJAVNOST_MESECEV) {
  const uploaded = !!docMap?.zdravniski;
  const examined = u?.zdravniski_datum ?? null;
  const expires = examined ? addMonths(examined, meseci) : null;
  const daysLeft = expires ? daysUntil(expires) : null;
  const valid = daysLeft != null && daysLeft >= 0;

  return {
    uploaded,
    examined,
    expires,
    daysLeft,
    valid,
    ok: uploaded && valid,
    /** Manj kot dva meseca do poteka — vredno opozoriti vnaprej. */
    expiringSoon: valid && daysLeft <= 60,
  };
}

/* ── Pot do izpita ──────────────────────────────────────────
   docMap === null pomeni „dokumenti se še nalagajo“ — takrat
   pogoja za zdravniško ne označimo kot nedosežen, ampak kot neznan,
   da kartica ob nalaganju ne utripa iz ✓ v ✗ in nazaj.            */

/** Najboljši opravljen poskus zaključnega testa, ali null. */
export function bestExamAttempt(attempts) {
  const passed = (attempts ?? []).filter((a) => a.passed);
  if (!passed.length) return null;
  // Najzgodnejši opravljen — datum, ko je tečajnik znanje dokazal.
  return passed.reduce((a, b) => (a.submitted_at <= b.submitted_at ? a : b));
}

export function examCheck(u, prog, fl, docMap, examAttempts, opts = {}) {
  const program = opts.program ?? DEFAULT_PROGRAM;
  const pogoji = { ...POGOJI_PRIVZETO, ...(opts.pogoji ?? {}) };
  const potrjeni = (fl ?? []).filter((f) => f.status === 'confirmed');
  const letov = potrjeni.length;
  const vzletisc = new Set(potrjeni.map((f) => f.vzletisce?.trim().toLowerCase()).filter(Boolean))
    .size;
  const age = ageOf(u?.datum_rojstva);
  const docsUnknown = docMap == null;
  const med = medicalStatus(u, docMap, pogoji.zdravniski_meseci);
  const test = bestExamAttempt(examAttempts);

  // En pogoj na sklop programa. Ključa 'zac' in 'nad' ostajata za Perutova
  // sklopa, da se jih drugod lahko sklicuje po imenu.
  const sklopItems = program.sklopi.map((s) => {
    const c = totalSubConfirmed(s.list, prog);
    const n = totalSubs(s.list);
    const key = s.key === 'zacetni' ? 'zac' : s.key === 'nadaljevalni' ? 'nad' : `sklop-${s.key}`;
    return { key, label: t('{sklop} potrjen', { sklop: s.label }), ok: n > 0 && c === n, detail: `${c}/${n}` };
  });

  const items = [
    ...sklopItems,
    {
      key: 'leti',
      label: t('Vsaj {n} potrjenih letov', { n: pogoji.min_letov }),
      ok: letov >= pogoji.min_letov,
      detail: `${letov}/${pogoji.min_letov}`,
    },
    {
      key: 'vzletisca',
      label: t('Leti na vsaj {n} različnih vzletiščih', { n: pogoji.min_vzletisc }),
      ok: vzletisc >= pogoji.min_vzletisc,
      detail: `${vzletisc}/${pogoji.min_vzletisc}`,
    },
    {
      key: 'zakljucni',
      label: t('Zaključni test opravljen'),
      ok: !!test,
      detail: test ? `${test.score}/${test.total} · ${fmtDate(test.submitted_at)}` : '',
    },
    {
      key: 'teorija',
      label: t('Teoretični izpit opravljen'),
      ok: !!u?.teorija_datum,
      detail: u?.teorija_datum ? fmtDate(u.teorija_datum) : '',
    },
    {
      key: 'starost',
      label: t('Starost vsaj {n} let', { n: pogoji.min_starost }),
      ok: age != null && age >= pogoji.min_starost,
      detail: age != null ? t('{n} let', { n: age }) : t('manjka datum rojstva'),
    },
    {
      key: 'zdravniski',
      // Kratko, ker gre v seznam pogojev. Pravilo o veljavnosti je razloženo
      // v zavihku Izpit; tu ga nosi že stolpec s podrobnostjo.
      label: t('Veljaven zdravniški za B kategorijo'),
      ok: med.ok,
      unknown: docsUnknown,
      detail: docsUnknown
        ? '…'
        : !med.uploaded
          ? t('ni naloženo')
          : !med.examined
            ? t('manjka datum')
            : !med.valid
              ? t('potekel {d}', { d: fmtDate(med.expires) })
              : t('velja do {d}', { d: fmtDate(med.expires) }),
    },
  ];

  const ok = items.filter((i) => i.ok).length;
  return {
    items,
    ok,
    total: items.length,
    ready: ok === items.length,
    med,
    test,
    letov,
    vzletisc,
    pogoji,
    /** Vse razen zdravniškega — za oznako v seznamu, kjer dokumentov ne
     *  nalagamo za vsakega tečajnika posebej. */
    readyExDocs: items.filter((i) => i.key !== 'zdravniski').every((i) => i.ok),
  };
}

/* ── Dnevnik prisotnosti ────────────────────────────────────
   Dan šolanja je vsak datum, na katerem se je zgodilo karkoli:
   vaja, let ali ročni vpis.                                      */

export function getSchoolDays(prog, fl, att, program = DEFAULT_PROGRAM) {
  const byDate = {};
  const ensure = (d) => {
    if (!byDate[d]) byDate[d] = { exercises: [], flights: 0, note: '', manual: false };
    return byDate[d];
  };

  program.all.forEach((ex) => {
    const ep = prog?.[ex.id];
    if (!ep?.subs) return;
    ex.subs.forEach((subTitle, i) => {
      const s = ep.subs[i];
      if (s?.date && (s.status === 'confirmed' || s.status === 'pending' || s.done)) {
        ensure(s.date).exercises.push({ exTitle: ex.title, subTitle, status: s.status });
      }
    });
  });

  (fl ?? []).forEach((f) => {
    if (!f.flight_date) return;
    ensure(f.flight_date).flights += 1;
  });

  (att ?? []).forEach((a) => {
    if (!a.attendance_date) return;
    const d = ensure(a.attendance_date);
    d.manual = true;
    d.manualId = a.id;
    if (a.note) d.note = a.note;
  });

  return Object.entries(byDate)
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([date, info]) => ({ date, ...info }));
}

/* ── Statistika knjižice letenja ────────────────────────────── */

export function flightStats(flights) {
  const list = flights ?? [];
  const confirmed = list.filter((f) => f.status === 'confirmed');
  return {
    confirmed: confirmed.length,
    pending: list.filter((f) => f.status === 'pending').length,
    sites: new Set(confirmed.map((f) => f.vzletisce).filter(Boolean)).size,
  };
}

/**
 * Zaporedne številke letov.
 *
 * Seznam je razvrščen od najnovejšega, zato je „let št. 1“ najstarejši,
 * ne prvi v seznamu. Stara verzija je uporabljala indeks v seznamu, zato
 * je najnovejši let dobil številko 1 — kar je bilo obratno od knjižice
 * na papirju.
 */
export function numberFlights(flights) {
  const list = flights ?? [];
  const n = list.length;
  return list.map((f, i) => ({ ...f, seq: n - i }));
}

/** Iskanje po tečajnikih: po številki (točno) ali po imenu/emailu (delno). */
export function matchStudent(u, query) {
  const q = query.trim().toLowerCase().replace(/^#/, '');
  if (!q) return true;
  if (String(u.stevilka ?? '') === q) return true;
  return `${u.ime ?? ''} ${u.priimek ?? ''} ${u.email ?? ''}`.toLowerCase().includes(q);
}
